/**
 * Learn content clusters: supplier-aligned topical hubs.
 *
 * Each cluster = a hub page (/learn/<key>) + its companion guides, wired so
 * the hub cross-links to every guide, each guide cross-links back to the hub
 * and its siblings (tight topical cluster for ranking), and the hub surfaces
 * the relevant affiliate supplier (leaderboard + product strip) via `topic`
 * and `projectType`. `pillars` link the cluster into our high-authority
 * ranking pages so equity flows both ways (internal-linking mesh).
 *
 * Adding a cluster: add an entry here + the guide JSON files + register the
 * guides in _registry.js + add slugs to lib/affiliateContext.js. The hub page
 * component and LearnGuidePage cross-links are fully config-driven.
 */

export const CLUSTERS = {
  'tableware-and-glassware': {
    eyebrow: 'Tableware & Glassware',
    h1: 'Setting a beautiful table',
    seoTitle: 'Tableware & Glassware Guides (Australia): Dinnerware & Glasses',
    seoDescription:
      'Australian tableware guides: how to choose dinnerware by material and the glasses most homes really need, to set an everyday table that still looks good for guests.',
    intro:
      'The plates you eat off and the glasses you drink from get used every day, yet they are also what make a table look considered when guests arrive. These guides cover choosing durable, good-looking dinnerware, and the handful of glasses worth owning.',
    topic: 'interior-styling',
    projectType: 'interior-styling',
    collectionName: 'Tableware & Glassware',
    heroImage: 'https://images.unsplash.com/photo-1562050344-f7ad946cee35?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Dinnerware, glassware and table pieces',
      intro:
        "Setting your table? Here's the kind of pieces owners reach for. You buy direct from the retailer at their price.",
    },
    guides: [
      { slug: 'dinnerware-buying-guide', title: 'Dinnerware buying guide', description: 'Choosing plates and bowls by material, what a place setting needs, and set vs open stock.', cta: 'Choose dinnerware' },
      { slug: 'glassware-buying-guide', title: 'Glassware buying guide', description: 'The glasses most homes really need, crystal vs glass, and stemmed vs stemless wine.', cta: 'Choose glassware' },
    ],
    pillars: [
      { href: '/learn/furnishing-your-home', title: 'Furnishing your home', description: 'Sofas, dining, rugs and lighting for the rest of the house.', cta: 'Browse the hub' },
      { href: '/learn/kitchen-and-cookware', title: 'Kitchen & cookware', description: 'Cookware and knives to kit out the kitchen.', cta: 'Browse the hub' },
      { href: '/learn/styling-your-new-home', title: 'Styling your new home', description: 'Pulling the whole look together once the furniture is in.', cta: 'Read the guide' },
    ],
  },

  'kitchen-and-cookware': {
    eyebrow: 'Kitchen & Cookware',
    h1: 'Kitting out the kitchen',
    seoTitle: 'Kitchen & Cookware Guides (Australia): Cookware & Knives',
    seoDescription:
      'Australian kitchen guides: how to choose cookware by material and cooktop, and the few kitchen knives worth owning, to kit out a new or renovated kitchen.',
    intro:
      'A working kitchen needs the right cookware and a few good knives far more than a drawer full of gadgets. These guides cover choosing pots and pans by material and cooktop, and the handful of knives that actually earn their place, so you can kit out a new or renovated kitchen well.',
    topic: 'interior-styling',
    projectType: 'interior-styling',
    collectionName: 'Kitchen & Cookware',
    heroImage: 'https://images.unsplash.com/photo-1661045542820-e0e02836be15?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Cookware, knives and kitchen tools',
      intro:
        "Kitting out the kitchen? Here's the kind of gear owners reach for. You buy direct from the retailer at their price.",
    },
    guides: [
      { slug: 'cookware-buying-guide', title: 'Cookware buying guide', description: 'Choosing pots and pans by material, the pieces you actually need, and matching cookware to your cooktop.', cta: 'Choose cookware' },
      { slug: 'kitchen-knife-buying-guide', title: 'Kitchen knife buying guide', description: 'The three knives most kitchens need, choosing steel and style, and keeping them sharp.', cta: 'Choose knives' },
    ],
    pillars: [
      { href: '/learn/furnishing-your-home', title: 'Furnishing your home', description: 'Sofas, dining, rugs and lighting for the rest of the house.', cta: 'Browse the hub' },
      { href: '/learn/kitchens', title: 'Kitchens guide', description: 'Planning, materials and costs for a new or renovated kitchen.', cta: 'Read the guide' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
    ],
  },

  'home-office': {
    eyebrow: 'Home Office',
    h1: 'Setting up a home office',
    seoTitle: 'Home Office Guides (Australia): Desks & Office Chairs',
    seoDescription:
      'Australian home office guides: how to choose a desk by size and type, and how to pick an ergonomic office chair, with the setup and posture details that matter for working from home.',
    intro:
      'A comfortable, productive home office comes down to two pieces, the desk and the chair, set up to suit your space and your body. These guides walk through sizing a desk, choosing between fixed, L-shaped and sit-stand, and picking an ergonomic chair that protects your back over long days.',
    topic: 'interior-styling',
    projectType: 'interior-styling',
    collectionName: 'Home Office',
    heroImage: 'https://images.unsplash.com/photo-1616440347437-b1c73416efc2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Desks, chairs and office storage',
      intro:
        "Setting up a home office? Here's the kind of gear owners reach for. You buy direct from the retailer at their price.",
    },
    guides: [
      { slug: 'office-desk-buying-guide', title: 'Office desk buying guide', description: 'Sizing a desk to your screens and space, and choosing fixed, L-shaped, compact or sit-stand.', cta: 'Choose a desk' },
      { slug: 'office-chair-buying-guide', title: 'Office chair buying guide', description: 'The ergonomic adjustments that matter, mesh vs leather, and matching the chair to your desk.', cta: 'Choose a chair' },
    ],
    pillars: [
      { href: '/learn/furnishing-your-home', title: 'Furnishing your home', description: 'Sofas, dining, rugs and lighting for the rest of the house.', cta: 'Browse the hub' },
      { href: '/learn/bedroom-furnishing', title: 'Bedroom furnishing', description: 'Bed frames and bedroom storage to finish the bedroom.', cta: 'Browse the hub' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
    ],
  },

  'bedroom-furnishing': {
    eyebrow: 'Bedroom Furnishing',
    h1: 'Furnishing the bedroom',
    seoTitle: 'Bedroom Furnishing Guides (Australia): Bed Frames & Bedroom Storage',
    seoDescription:
      'Australian bedroom furnishing guides: how to choose a bed frame by size, style and storage, and how to plan bedroom storage with wardrobes, tallboys, dressers and bedside tables.',
    intro:
      'Beyond the mattress, it is the bed frame and the storage that make a bedroom feel finished and function day to day. These guides walk through choosing a bed frame that fits your room and style, and planning bedroom storage so clothes and clutter have a home.',
    topic: 'interior-styling',
    projectType: 'interior-styling',
    collectionName: 'Bedroom Furnishing',
    heroImage: 'https://images.unsplash.com/photo-1648668425549-84b695d2ec2a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Beds, storage and bedroom pieces',
      intro:
        "Furnishing the bedroom? Here's the kind of pieces owners reach for. You buy direct from the retailer at their price.",
    },
    guides: [
      { slug: 'bed-frame-buying-guide', title: 'Bed frame buying guide', description: 'Sizing a bed to your room, choosing a style and headboard, and whether to add storage.', cta: 'Choose a bed frame' },
      { slug: 'bedroom-storage-guide', title: 'Bedroom storage guide', description: 'Planning wardrobes, tallboys, dressers and bedside tables so everything has a home.', cta: 'Plan your storage' },
    ],
    pillars: [
      { href: '/learn/furnishing-your-home', title: 'Furnishing your home', description: 'Sofas, dining, rugs and lighting for the rest of the house.', cta: 'Browse the hub' },
      { href: '/learn/styling-your-new-home', title: 'Styling your new home', description: 'Pulling the look together once the furniture is in.', cta: 'Read the guide' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
    ],
  },

  'furnishing-your-home': {
    eyebrow: 'Furnishing Your Home',
    h1: 'Furnishing your home, room by room',
    seoTitle: 'Furnishing Your Home Guides (Australia): Sofas, Dining, Rugs & Lighting',
    seoDescription:
      'Practical Australian buying guides for furnishing a new home or renovation: how to choose a sofa, a dining table, the right rug and layered home lighting, with sizing, materials and placement.',
    intro:
      'Once the build or renovation is done, the next job is turning empty rooms into a home. These guides walk through the pieces that anchor each space, the sofa, the dining table, the rug underfoot and the lighting throughout, with the sizing, materials and placement tips that make a room feel finished.',
    topic: 'interior-styling',
    projectType: 'interior-styling',
    collectionName: 'Furnishing Your Home',
    heroImage: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Furniture, lighting & rugs to finish your rooms',
      intro:
        "Furnishing your home? Here's the kind of pieces owners reach for. You buy direct from the retailer at their price.",
    },
    guides: [
      { slug: 'sofa-buying-guide', title: 'Sofa buying guide', description: 'Sizing a couch to your room, fabric vs leather, and comparing 2-seaters, 3-seaters, modulars and sofa beds.', cta: 'Choose a sofa' },
      { slug: 'dining-table-buying-guide', title: 'Dining table buying guide', description: 'Sizing and shape, timber vs marble vs glass, and matching dining chairs.', cta: 'Choose a table' },
      { slug: 'rug-buying-guide', title: 'Rug buying guide', description: 'Getting the size right, wool vs synthetic vs jute, and placement in living and dining rooms.', cta: 'Choose a rug' },
      { slug: 'home-lighting-buying-guide', title: 'Home lighting buying guide', description: 'Layering ambient, task and accent light, choosing fittings, and getting warmth and dimming right.', cta: 'Light your home' },
    ],
    pillars: [
      { href: '/learn/styling-your-new-home', title: 'Styling your new home', description: 'Pulling the look together once the furniture is in.', cta: 'Read the guide' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
      { href: '/renovations', title: 'Renovations', description: 'Planning, costs and trades for renovating an Australian home.', cta: 'Read the guide' },
    ],
  },

  'pergolas-and-outdoor-living': {
    eyebrow: 'Pergolas & Outdoor Living',
    h1: 'Pergolas & louvered outdoor living',
    seoTitle: 'Pergolas & Louvered Outdoor Living Guides (Australia)',
    seoDescription:
      'Guides for planning a louvered pergola or outdoor room in Australia: choosing a model, pergola vs verandah, enclosing with walls and screens, and adding lighting and heating for year-round use.',
    intro:
      'A louvered pergola turns a bare deck or patio into a year-round outdoor room you actually use. These guides cover choosing the right model, how a pergola compares to a verandah, enclosing the space with walls and screens, and adding lighting and heating so it works later into the night and further into the cooler months.',
    topic: 'verandahs-pergolas',
    projectType: 'verandahs-pergolas',
    collectionName: 'Pergolas & Outdoor Living',
    heroImage: 'https://images.unsplash.com/photo-1762608675427-09ac2dbd1540?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Handy gear for an outdoor room',
      intro:
        "Building out your outdoor space? Here's the practical gear owners reach for. You buy direct from the supplier at their price.",
    },
    guides: [
      { slug: 'louvered-pergola-buying-guide', title: 'Louvered pergola buying guide', description: 'How adjustable roofs work, sizing to your space, and choosing between Series 3, 4, Pro and Pro Max.', cta: 'Choose a pergola' },
      { slug: 'pergola-vs-verandah', title: 'Pergola vs verandah', description: 'Open pergola, louvered-roof pergola or solid verandah \u2014 which suits your backyard and climate.', cta: 'Compare the options' },
      { slug: 'enclosing-a-pergola', title: 'Enclosing a pergola', description: 'Glass walls, zip screens and slat walls to make an outdoor room usable in more weather.', cta: 'Enclose the space' },
      { slug: 'pergola-lighting-and-heating', title: 'Pergola lighting & heating', description: 'LED lighting, lanterns and beam heaters to use the space later into the night and the cooler months.', cta: 'Light and heat it' },
    ],
    pillars: [
      { href: '/verandahs-pergolas', title: 'Verandahs & pergolas', description: 'Costs, approvals and choosing a builder for a verandah or pergola in Australia.', cta: 'Read the guide' },
      { href: '/learn/landscaping', title: 'Landscaping guide', description: 'Materials, costs and how to plan landscaping for an Australian home.', cta: 'Read the guide' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
    ],
  },

  'bedroom-and-sleep': {
    mrecSupplier: 'maisonbalzac',
    eyebrow: 'Bedroom & Sleep',
    h1: 'Bedrooms & sleep in a new home',
    seoTitle: 'Bedroom & Sleep: New Home Setup Guides (Australia)',
    seoDescription:
      'Everything on setting up bedrooms in a new Australian home: mattress sizing, the main bedroom moving-in checklist, nursery setup and turning a spare room into a guest room.',
    intro:
      'The bedroom is usually the last room sorted when you move in, yet it is the one you spend a third of your life in. These guides cover getting the mattress size right, setting up the main bedroom, a nursery and a guest room, with realistic Australian costs.',
    topic: 'bedroom',
    projectType: 'bedroom',
    collectionName: 'Bedroom & Sleep',
    heroImage: 'https://images.unsplash.com/photo-1657040899594-34f4b6739988?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Handy gear for the bedroom',
      intro:
        "Setting up a bedroom? Here's the practical gear owners reach for. You buy direct from the supplier at their price.",
    },
    guides: [
      { slug: 'setting-up-your-bedroom', title: 'Setting up your bedroom', description: 'A moving-in checklist for the main bedroom: mattress, bedding, blackout, airflow and cleaning.', cta: 'Read the checklist' },
      { slug: 'mattress-sizing-guide', title: 'Mattress sizing guide', description: 'Every Australian mattress size with exact dimensions and the room each one needs.', cta: 'Compare sizes' },
      { slug: 'nursery-setup', title: 'Nursery setup', description: 'Set up a safe, calm nursery in a new home: cot, airflow, blackout and the gear that matters.', cta: 'Plan the nursery' },
      { slug: 'guest-room-setup', title: 'Guest room setup', description: 'Turn a spare room into a comfortable guest room on a budget, from mattress to bedside basics.', cta: 'Set up the room' },
    ],
    pillars: [
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
      { href: '/house-and-land-adelaide', title: 'House & land packages', description: 'Browse live house and land packages and what is really included.', cta: 'Browse packages' },
      { href: '/home-designs', title: 'Home designs', description: 'Explore floor plans and designs to match your block and budget.', cta: 'Explore designs' },
    ],
  },

  'outdoor-and-yard': {
    eyebrow: 'Outdoor & Yard',
    h1: 'Setting up your outdoor space',
    seoTitle: 'Outdoor & Yard: New Home Backyard Setup Guides (Australia)',
    seoDescription:
      'Practical guides for setting up a new Australian backyard: establishing a new lawn, setting up the yard in the right order, pressure washing, and outdoor entertaining.',
    intro:
      'A new build leaves you with a bare, compacted block and a long list of outdoor jobs. These guides cover getting it right in the right order: establishing a lawn that survives summer, setting up the yard, keeping surfaces clean, and building an outdoor space you actually use.',
    topic: 'landscaping',
    projectType: 'landscaping',
    collectionName: 'Outdoor & Yard',
    heroImage: 'https://images.unsplash.com/photo-1715247018235-82cb9d34ddbd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Handy gear for the yard',
      intro:
        "Tackling the yard yourself? Here's the practical outdoor gear owners reach for. You buy direct from the supplier at their price.",
    },
    guides: [
      { slug: 'establishing-a-new-lawn', title: 'Establishing a new lawn', description: 'Fixing compacted builder\u2019s soil, choosing turf or seed, and watering a new lawn in.', cta: 'Grow the lawn' },
      { slug: 'setting-up-your-backyard', title: 'Setting up your backyard', description: 'The right order for a new yard: drainage, fencing, lawn, paths and storage.', cta: 'Plan the yard' },
      { slug: 'pressure-washing-guide', title: 'Pressure washing & exterior cleaning', description: 'The right pressure for driveways, paving, decking and render, and what to never blast.', cta: 'Clean it right' },
      { slug: 'outdoor-entertaining-setup', title: 'Outdoor entertaining setup', description: 'Shade, weatherproof furniture, heating and cooling for an alfresco you use year round.', cta: 'Set it up' },
    ],
    pillars: [
      { href: '/learn/landscaping', title: 'Landscaping guide', description: 'Materials, costs and how to plan landscaping for an Australian home.', cta: 'Read the guide' },
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
    ],
  },

  'home-cleaning-and-move-in': {
    mrecSupplier: 'maisonbalzac',
    eyebrow: 'Cleaning & Move-In',
    h1: 'Cleaning & moving into a new home',
    seoTitle: 'New-Home Cleaning & Move-In Guides (Australia)',
    seoDescription:
      'Practical guides for cleaning and moving into a new Australian build: the pre-move-in clean, a room-by-room checklist, managing construction dust, and choosing a robot vacuum.',
    intro:
      'A new home looks spotless at handover but is coated in construction dust that keeps surfacing for weeks. These guides cover cleaning the house before you move in, a room-by-room checklist, staying ahead of the dust, and choosing gear that keeps a new home clean with less effort.',
    topic: 'home cleaning move in',
    projectType: 'home-cleaning',
    collectionName: 'Cleaning & Move-In',
    heroImage: 'https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Gear that keeps a new home clean',
      intro:
        "Staying on top of a new home? Here's the practical gear owners reach for. You buy direct from the supplier at their price.",
    },
    guides: [
      { slug: 'cleaning-a-new-build-before-moving-in', title: 'Cleaning before you move in', description: 'What a builder\u2019s clean misses, the right order to clean, and dealing with dust and haze.', cta: 'Clean first' },
      { slug: 'new-home-cleaning-checklist', title: 'Room-by-room cleaning checklist', description: 'A practical checklist for the kitchen, bathrooms, bedrooms, living areas and garage.', cta: 'Use the checklist' },
      { slug: 'managing-construction-dust', title: 'Managing construction dust', description: 'Why dust keeps resurfacing after handover and the low-effort ways to stay ahead of it.', cta: 'Beat the dust' },
      { slug: 'robot-vacuum-buying-guide', title: 'Robot vacuum buying guide', description: 'What actually matters: suction, self-emptying, mopping and navigation, plus price bands.', cta: 'Choose a vacuum' },
    ],
    pillars: [
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
      { href: '/house-and-land-adelaide', title: 'House & land packages', description: 'Browse live house and land packages and what is really included.', cta: 'Browse packages' },
    ],
  },

  'styling-your-new-home': {
    mrecSupplier: 'maisonbalzac',
    eyebrow: 'Interior Styling',
    h1: 'Styling your new home',
    seoTitle: 'Styling Your New Home: Kitchen, Dining & Living (Australia)',
    seoDescription:
      'Practical guides for styling the finished rooms of a new Australian home: the kitchen, the dining table, the living room and layering home fragrance, with realistic costs.',
    intro:
      'Once the build is done and the furniture is in, styling is what makes a new house feel like a finished home. These guides cover curating the kitchen, setting a dining table, styling the living room and layering scent room by room, with realistic Australian costs.',
    topic: 'interior',
    projectType: 'interior-styling',
    collectionName: 'Interior Styling',
    heroImage: 'https://images.unsplash.com/photo-1609775324322-6dafcbd21515?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0',
    strip: {
      heading: 'Pieces to style your new home',
      intro:
        'Styling the finished rooms? Here are the glassware, vases and candles owners reach for. You buy direct from the supplier at their price.',
    },
    guides: [
      { slug: 'styling-a-new-kitchen', title: 'Styling a new kitchen', description: 'Curating the benchtop, everyday glassware and open-shelf styling in a new kitchen.', cta: 'Style the kitchen' },
      { slug: 'dining-table-styling', title: 'Dining table styling', description: 'Everyday and occasion table settings with glassware, carafes, napkins and centrepieces.', cta: 'Set the table' },
      { slug: 'living-room-styling', title: 'Living room styling', description: 'Shelf and coffee-table styling, vases and candles to make a new living room feel finished.', cta: 'Style the living room' },
      { slug: 'home-fragrance-guide', title: 'Home fragrance guide', description: 'Layering candles, incense and room sprays room by room without overpowering the home.', cta: 'Layer the scent' },
    ],
    pillars: [
      { href: '/building-a-new-home', title: 'Building a new home', description: 'The full journey from planning to handover for a new Australian home.', cta: 'Read the guide' },
      { href: '/learn/bedroom-and-sleep', title: 'Bedroom & sleep', description: 'Setting up and styling the bedrooms in a new home.', cta: 'Browse the hub' },
    ],
  },
};

// guide slug -> cluster key, for the cross-link strip rendered on each guide.
export const CLUSTER_FOR_GUIDE = Object.entries(CLUSTERS).reduce((acc, [key, c]) => {
  c.guides.forEach((g) => { acc[g.slug] = key; });
  return acc;
}, {});

export const getCluster = (key) => CLUSTERS[key] || null;
